import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import type { Role } from '@/shared/auth/sessionStore';
import { aAuMoins } from '@/shared/auth/sessionStore';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * REGISTRE DE MODULES — la signature de l'application (§3)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Le back est un monolithe modulaire en tranches verticales ; le front en est le
 * miroir. Chaque feature declare ce qu'elle est — un identifiant, un libelle, une
 * icone, des routes — et NE SAIT RIEN des autres.
 *
 * Au demarrage, apres authentification et choix de l'entreprise, l'application
 * interroge `ModuleActivation` cote API et ne conserve que les modules actifs.
 * La barre laterale ET le routeur sont construits a partir de cette seule liste.
 *
 * CONSEQUENCE VOULUE : un module desactive n'a ni entree de menu, ni route. Taper
 * son URL a la main ne donne rien — ce n'est pas un menu masque devant des pages
 * toujours accessibles.
 *
 * AJOUTER UN MODULE : creer la feature, exporter sa declaration, l'ajouter au
 * tableau `MODULES`. Aucun autre fichier ne bouge.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Icone Lucide, ou tout composant acceptant une classe. */
export type IconeModule = ComponentType<{ className?: string }>;

/** Fabrique d'un module de page, telle que `import()` la rend. */
type FabriquePage = () => Promise<{ default: ComponentType }>;

/**
 * Page paresseuse PRECHARGEABLE.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE DETOUR PLUTOT QU'UN `lazy()` NU
 *
 * React Router traite une navigation comme une TRANSITION. Pendant qu'un module
 * de page se telecharge, React conserve l'ecran precedent au lieu d'afficher le
 * repli du `Suspense` : c'est voulu de sa part — mieux vaut un ecran ancien
 * qu'un squelette clignotant.
 *
 * L'effet de bord est ingrat. On clique un projet, l'URL change, et la LISTE
 * reste a l'ecran sans le moindre indicateur. Rien ne distingue « ca charge » de
 * « le clic n'a pas pris » : l'utilisateur recharge la page, et decouvre que la
 * fiche s'affiche tres bien.
 *
 * On ne peut pas forcer le repli a s'afficher : la transition vient de React
 * Router, qui n'offre pas de le desactiver. On supprime donc l'attente elle-meme
 * — les modules sont recuperes pendant les temps morts, apres le premier rendu.
 * Au clic, le module est deja la et la bascule est immediate.
 *
 * `lazy()` ne rend pas sa fabrique : on la conserve a cote pour pouvoir la
 * rejouer. L'appeler deux fois est sans risque, `import()` met en cache.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export interface PageParesseuse extends LazyExoticComponent<ComponentType> {
  readonly precharger: FabriquePage;
}

export function page(fabrique: FabriquePage): PageParesseuse {
  const composant = lazy(fabrique) as PageParesseuse;

  return Object.assign(composant, { precharger: fabrique });
}

/**
 * Recupere les modules de page pendant les temps morts du navigateur.
 *
 * APRES le premier rendu et seulement a l'inactivite : le chargement initial
 * n'est pas alourdi (§7), et un navigateur sans `requestIdleCallback` retombe sur
 * un delai franc plutot que de tout demander pendant la peinture.
 */
export function prechargerPages(routes: readonly RouteModule[]): () => void {
  const lancer = () => {
    for (const route of routes) {
      const composant = route.composant as Partial<PageParesseuse>;

      // Une erreur reseau ici ne doit rien casser : c'est une optimisation, pas
      // un chargement dont la page depend. React.lazy redemandera au besoin.
      void composant.precharger?.().catch(() => undefined);
    }
  };

  if (typeof requestIdleCallback === 'function') {
    const jeton = requestIdleCallback(lancer, { timeout: 2000 });
    return () => cancelIdleCallback(jeton);
  }

  const jeton = setTimeout(lancer, 800);
  return () => clearTimeout(jeton);
}

export interface RouteModule {
  /** Chemin relatif a la racine de l'application, sans barre oblique initiale. */
  chemin: string;
  /** Page chargee paresseusement : une feature non visitee ne pese rien (§7). */
  composant: LazyExoticComponent<ComponentType>;
  /** Role minimal exige. Absent = tout membre, meme en lecture seule. */
  roleRequis?: Role;
  /** Vrai pour la route affichee lorsqu'on clique l'entree de menu du module. */
  estAccueil?: boolean;
  /**
   * Libelle d'entree de navigation.
   *
   * UN MODULE PEUT AVOIR PLUSIEURS ENTREES DE MENU, et c'est une correction.
   * La navigation n'affichait qu'une entree par module — celle de `estAccueil`.
   * Le module Sales couvre les devis ET les factures : seules les factures
   * etaient donc atteignables, les devis n'existant qu'a l'URL. Une route livree
   * mais sans chemin pour y arriver est une route absente.
   *
   * Toute route portant ce libelle apparait dans la barre laterale. Sans libelle
   * sur aucune route, on retombe sur l'entree unique d'origine.
   */
  libelleNav?: string;
  /**
   * Range l'entree dans le menu « Parametres de l'entreprise » plutot que dans la
   * barre laterale.
   *
   * La barre laterale porte le TRAVAIL QUOTIDIEN — clients, devis, factures,
   * projets. Les reglages d'entreprise, les modules, l'equipe, l'abonnement et le
   * RGPD sont des ecrans qu'on ouvre quelques fois par an : les laisser dans la
   * navigation principale les mettait au meme rang que les ecrans consultes dix
   * fois par jour, et allongeait la liste d'autant.
   */
  dansParametres?: boolean;
}

export interface DeclarationModule {
  /** Doit correspondre EXACTEMENT a la `cleModule` du back (`clients`, `sales`...). */
  id: string;
  libelle: string;
  icone: IconeModule;
  /** Ordre de repli, si l'API n'en fournit pas. */
  ordre: number;
  routes: RouteModule[];
  /** Role minimal pour voir le module dans la navigation. */
  roleRequis?: Role;
  /**
   * Modules TOUJOURS actifs, meme absents de `ModuleActivation`.
   *
   * `platform` en fait partie : sans lui, une entreprise ayant tout desactive
   * n'aurait plus aucun moyen de rouvrir ses modules. Un cul-de-sac dont on ne
   * sort que par la base de donnees n'est pas un reglage acceptable.
   */
  toujoursActif?: boolean;
}

/** Module retenu apres filtrage, avec l'ordre effectivement applique. */
export interface ModuleActif extends DeclarationModule {
  ordreEffectif: number;
}

/** Etat d'activation tel que l'API le renvoie. */
export interface ActivationApi {
  cleModule: string;
  actif: boolean;
  ordre: number;
}

/**
 * Croise les modules declares avec les activations de l'entreprise et le role.
 *
 * FONCTION PURE : c'est ce qui la rend testable sans monter l'application ni
 * simuler le reseau — et le §9 en exige des tests.
 *
 * Trois filtres, dans cet ordre :
 *  1. activation cote entreprise (sauf module `toujoursActif`) ;
 *  2. role suffisant ;
 *  3. tri par l'ordre de l'API, avec repli sur l'ordre declare.
 *
 * Un module ACTIF cote API mais inconnu du front est ignore sans bruit : le back
 * peut livrer un module avant que le front ne l'implemente.
 */
export function resoudreModules(
  declarations: readonly DeclarationModule[],
  activations: readonly ActivationApi[] | undefined,
  role: Role | null,
): ModuleActif[] {
  const parCle = new Map(activations?.map((activation) => [activation.cleModule, activation]));

  return declarations
    .filter((module) => {
      if (module.roleRequis && !aAuMoins(role, module.roleRequis)) {
        return false;
      }

      if (module.toujoursActif) {
        return true;
      }

      const activation = parCle.get(module.id);

      // Aucune activation connue : on n'affiche pas. Mieux vaut un menu incomplet
      // qu'une entree qui mene a des pages vides parce que le module n'est pas
      // provisionne cote entreprise.
      return activation?.actif === true;
    })
    .map((module) => ({
      ...module,
      ordreEffectif: parCle.get(module.id)?.ordre ?? module.ordre,
    }))
    .sort((a, b) => a.ordreEffectif - b.ordreEffectif || a.libelle.localeCompare(b.libelle, 'fr'));
}

/** Routes des modules actifs, aplaties, prêtes pour le routeur. */
export function routesActives(modules: readonly ModuleActif[]): (RouteModule & { moduleId: string })[] {
  return modules.flatMap((module) =>
    module.routes.map((route) => ({ ...route, moduleId: module.id })),
  );
}

/** Chemin d'accueil d'un module, cible de son entree de menu. */
export function cheminAccueil(module: DeclarationModule): string {
  const accueil = module.routes.find((route) => route.estAccueil) ?? module.routes[0];
  return `/${accueil?.chemin ?? ''}`;
}

/** Une entree de la barre laterale. */
export interface EntreeNavigation {
  readonly chemin: string;
  readonly libelle: string;
  readonly icone: IconeModule;
  /** Vrai si l'entree represente le module entier (cas d'un module a une seule vue). */
  readonly estRacine: boolean;
}

/**
 * Entrees de navigation d'un module.
 *
 * Un module qui declare des `libelleNav` en produit une par route ainsi marquee ;
 * les autres gardent leur entree unique. La navigation reste donc construite
 * exclusivement depuis les declarations (§3), sans liste codee ailleurs.
 */
export function entreesNavigation(module: ModuleActif): EntreeNavigation[] {
  const nommees = module.routes.filter((route) => route.libelleNav && !route.dansParametres);

  if (nommees.length === 0) {
    /*
      Un module dont TOUTES les entrees vont aux parametres n'apparait pas dans la
      barre laterale. Sans ce test, on lui rendrait ici une entree de repli et il
      figurerait aux deux endroits.
    */
    if (module.routes.some((route) => route.dansParametres)) return [];

    return [
      { chemin: cheminAccueil(module), libelle: module.libelle, icone: module.icone, estRacine: true },
    ];
  }

  return nommees.map((route) => ({
    chemin: `/${route.chemin}`,
    libelle: route.libelleNav as string,
    icone: module.icone,
    estRacine: false,
  }));
}

/**
 * Entrees du menu « Parametres de l'entreprise ».
 *
 * Construites depuis les MEMES declarations que la barre laterale : un module
 * desactive disparait des deux, et il n'existe nulle part de liste de reglages
 * codee a la main (§3).
 */
export function entreesParametres(modules: readonly ModuleActif[]): EntreeNavigation[] {
  return modules.flatMap((module) =>
    module.routes
      .filter((route) => route.dansParametres && route.libelleNav)
      .map((route) => ({
        chemin: `/${route.chemin}`,
        libelle: route.libelleNav as string,
        icone: module.icone,
        estRacine: false,
      })),
  );
}

/**
 * Premiere destination apres connexion.
 *
 * On evite de coder « /clients » en dur : si Clients est desactive, l'utilisateur
 * atterrirait sur une page inexistante. On prend le premier module reellement actif.
 */
export function cheminParDefaut(modules: readonly ModuleActif[]): string {
  const premier = modules[0];
  return premier ? cheminAccueil(premier) : '/entreprise';
}
